import * as React from "react"
import {
  Button,
  Heading,
  StyledPanelBodySection,
  StyledPanelBoilerplate,
  ThemeCss,
} from "gatsby-interface"
import { gatsbyCloudCdnIntegrationConfig as text } from "@modules/locales/default.js"
import {
  HostingDetails,
  HostingCertificate,
  CertificateStatus,
} from "@modules/graphql/types"
import { CustomCertificateForm } from "./CustomCertificateForm"
import {
  SslCertificateDetails,
  SslCertificateIsGenerating,
} from "../../shared/components/SslCertificateDetails"
import { RemoveCertificateForm } from "./RemoveCertificateForm"
import { useTracker, SegmentEventType } from "@modules/analytics"

const headerCss: ThemeCss = theme => ({
  alignItems: `center`,
  borderBottom: `1px solid ${theme.colors.grey[20]}`,
  display: `flex`,
  justifyContent: `space-between`,
  paddingBottom: theme.space[2],
  marginTop: theme.space[11],
})

const headingCss: ThemeCss = theme => ({
  fontFamily: theme.fonts.body,
  fontSize: theme.fontSizes[2],
  fontWeight: theme.fontWeights.semiBold,
})

const listCss: ThemeCss = theme => ({
  margin: 0,
  marginTop: theme.space[5],
  listStyleType: `none`,
})

const itemCss: ThemeCss = theme => ({
  marginBottom: theme.space[7],

  "&:last-of-type": {
    marginBottom: 0,
  },
})

export type SslCertificatesProps = {
  hostingDetails: HostingDetails
  siteId: string
  allowToEdit?: boolean
}

export function SslCertificates({
  hostingDetails,
  siteId,
  allowToEdit,
  ...rest
}: SslCertificatesProps) {
  const { trackSegment, trackAction } = useTracker()
  const [showCertForm, setShowCertForm] = React.useState<boolean>(false)
  const [
    certificateToRemove,
    setCertificateToRemove,
  ] = React.useState<HostingCertificate | null>(null)
  const certificates = hostingDetails.certificates ?? []

  const domainsWithRequestedCert =
    hostingDetails.domains?.filter(item => {
      return item.certificateStatus === CertificateStatus.Requested
    }) ?? []

  return (
    <div {...rest}>
      <div css={headerCss}>
        <Heading as="h4" css={headingCss}>
          {text.headers.sslCertificates}
        </Heading>
        {allowToEdit && (
          <Button
            onClick={() => setShowCertForm(true)}
            variant="GHOST"
            size="S"
          >
            {text.actions.useCustomCertificate}
          </Button>
        )}
      </div>
      <ul css={listCss}>
        {domainsWithRequestedCert.map(({ id }) => {
          return (
            <li css={itemCss} key={id}>
              <SslCertificateIsGenerating domain={id} />
            </li>
          )
        })}

        {certificates.map(cert => {
          const id = cert?.id

          if (!id) {
            return null
          }

          return (
            <li css={itemCss} key={id}>
              <SslCertificateDetails
                certificate={cert}
                onRemove={() => setCertificateToRemove(cert)}
                allowToEdit={allowToEdit}
              />
            </li>
          )
        })}
      </ul>

      {!!certificateToRemove && (
        <RemoveCertificateForm
          siteId={siteId}
          certificate={certificateToRemove}
          onDismiss={() => setCertificateToRemove(null)}
          onComplete={() => {
            setCertificateToRemove(null)

            trackAction({
              eventType: `TRACK_EVENT`,
              name: `SSL Certificate removed`,
              uiSource: `Site Settings`,
            })
            trackSegment({
              type: SegmentEventType.Track,
              event: "SSL Certificate removed",
              properties: {
                location: "Site Settings",
              },
            })
          }}
        />
      )}

      {showCertForm && (
        <StyledPanelBoilerplate
          isOpen={showCertForm}
          aria-label={text.headers.addDomain}
          onClose={() => setShowCertForm(false)}
          header={text.headers.useCustomCertificate}
        >
          <StyledPanelBodySection>
            <CustomCertificateForm
              siteId={siteId}
              onComplete={() => {
                setShowCertForm(false)

                trackAction({
                  eventType: `TRACK_EVENT`,
                  name: `SSL Certificate uploaded`,
                  uiSource: `Site Settings`,
                })
                trackSegment({
                  type: SegmentEventType.Track,
                  event: "SSL Certificate uploaded",
                  properties: {
                    location: "Site Settings",
                  },
                })
              }}
              onCancel={() => setShowCertForm(false)}
            />
          </StyledPanelBodySection>
        </StyledPanelBoilerplate>
      )}
    </div>
  )
}
